import { AlertTriangle, Info, Loader2, Check, Ban, Undo2, RotateCcw, type LucideIcon } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// 모달 톤(아이콘 메달리온 + 대상 칩 레일 색). 동작 성격에 맞춰 차분하게:
// positive=확정(초록 체크) · caution=되돌림(amber) · info=복구(파랑) · neutral=제외(slate, 위협적이지 않게)
// · warn=경고성 진행 확인(amber ⚠ — 되돌림 화살표가 어울리지 않는 일반 경고용, 예: 부분 입금 출고).
export type ConfirmTone = 'positive' | 'caution' | 'neutral' | 'info' | 'warn';

const TONE: Record<ConfirmTone, { wrap: string; icon: string; rail: string; Icon: LucideIcon }> = {
  positive: { wrap: 'bg-emerald-50', icon: 'text-emerald-600', rail: 'bg-emerald-500', Icon: Check },
  caution: { wrap: 'bg-amber-50', icon: 'text-amber-600', rail: 'bg-amber-500', Icon: Undo2 },
  neutral: { wrap: 'bg-slate-100', icon: 'text-slate-500', rail: 'bg-slate-300', Icon: Ban },
  info: { wrap: 'bg-blue-50', icon: 'text-blue-600', rail: 'bg-blue-500', Icon: RotateCcw },
  warn: { wrap: 'bg-amber-50', icon: 'text-amber-600', rail: 'bg-amber-500', Icon: AlertTriangle },
};

const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;

interface ConfirmGuardProps {
  open: boolean;
  title: string;
  warning?: string;
  // warning(amber ⚠ = 실수 가능성 챌린지)과 달리 notice는 차분한 안내(blue ⓘ) — 부분입금처럼
  // '정상 플로우인데 맥락 설명이 필요한' 확정에 사용. 심리적 저항 없이 안심시키는 톤.
  notice?: string;
  confirmLabel: string;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  // 다이얼로그 부연 설명. 미지정 시 확정/되돌림 기본문구(주문 상태 변경). 제외/복구는 주문을 안 건드리므로 덮어쓴다.
  description?: string;
  // 아이콘 메달리온 + 대상 칩 레일 색을 결정. 기본 neutral.
  tone?: ConfirmTone;
  // 대상 입금 요약 칩(₩금액 hero + 입금자·은행). /deposits 행 비주얼과 동일 톤으로 '무엇을' 처리하는지 앵커.
  subject?: { amount: number; label: string };
  // 금액 breakdown(정산서 금액/기존 입금/남은 잔액 등) — subject 칩 하단에 표로 렌더.
  // 타이틀에 숫자를 욱여넣지 않기 위한 구조(상세 패널 money-box와 동일 시각 패턴). strong=합계행.
  rows?: Array<{ label: string; value: string; strong?: boolean }>;
}

export function ConfirmGuard({
  open,
  title,
  warning,
  notice,
  confirmLabel,
  loading,
  onConfirm,
  onCancel,
  description = '이 작업은 주문 상태를 변경합니다.',
  tone = 'neutral',
  subject,
  rows,
}: ConfirmGuardProps) {
  const isUndo = confirmLabel === '되돌림';
  const t = TONE[tone];
  const ToneIcon = t.Icon;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        // Only react to programmatic/overlay close attempts; ignore while loading.
        if (!next && !loading) onCancel();
      }}
    >
      <AlertDialogContent className="max-w-md gap-0 overflow-hidden p-0">
        <div className="p-5">
          <AlertDialogHeader>
            <div className="flex items-start gap-3.5 text-left">
              <span
                className={cn('mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full', t.wrap)}
                aria-hidden
              >
                <ToneIcon className={cn('h-5 w-5', t.icon)} />
              </span>
              <div className="min-w-0 flex-1 space-y-1">
                <AlertDialogTitle className="text-[15px] font-semibold leading-relaxed text-slate-900 break-keep">
                  {title}
                </AlertDialogTitle>
                <AlertDialogDescription className="text-[13px] leading-relaxed text-slate-500 break-keep">
                  {description}
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>

          {/* 대상 입금 요약 칩 — '무엇을' 처리하는지 시각 앵커(행 비주얼과 동일 톤).
              rows가 있으면 같은 카드 안에 breakdown 표를 이어 붙인다(박스 2개 나열 방지).
              rows는 subject와 독립(참고 카드 경로는 subject 없이 rows만 전달). */}
          {(subject || (rows && rows.length > 0)) && (
            <div className="mt-4 rounded-xl bg-slate-50 ring-1 ring-slate-200/80">
              {subject && (
                <div className="flex items-center gap-3 py-2.5 pl-3 pr-4">
                  <span className={cn('h-9 w-[3px] shrink-0 rounded-full', t.rail)} aria-hidden />
                  <div className="min-w-0">
                    <div className="text-[17px] font-bold leading-none tabular-nums text-slate-900">
                      {won(subject.amount)}
                    </div>
                    <div className="mt-1.5 truncate text-xs text-slate-500">{subject.label}</div>
                  </div>
                </div>
              )}
              {rows && rows.length > 0 && (
                <div
                  className={cn(
                    'px-4 py-2.5 text-[13px] tabular-nums text-slate-700',
                    subject && 'border-t border-slate-200/80',
                  )}
                >
                  {rows.map((r) => (
                    <div
                      key={r.label}
                      className={cn(
                        'flex items-center justify-between py-0.5',
                        r.strong && 'mt-1 border-t border-slate-200 pt-1.5 font-semibold text-slate-900',
                      )}
                    >
                      <span className={r.strong ? undefined : 'text-slate-500'}>{r.label}</span>
                      <span>{r.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {warning ? (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="whitespace-pre-line break-keep leading-relaxed">{warning}</span>
            </div>
          ) : null}

          {notice ? (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              {/* whitespace-pre-line: notice 문자열의 \n을 줄바꿈으로 렌더(다문장 안내). */}
              <span className="whitespace-pre-line break-keep leading-relaxed">{notice}</span>
            </div>
          ) : null}
        </div>

        <AlertDialogFooter className="gap-2 border-t border-slate-100 bg-slate-50/60 px-5 py-3.5">
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            disabled={loading}
            onClick={onCancel}
          >
            취소
          </Button>
          <Button
            type="button"
            variant={isUndo ? 'secondary' : 'default'}
            className="min-h-11"
            disabled={loading}
            onClick={onConfirm}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                처리 중…
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
